import React, { useEffect, useRef, useCallback, useMemo } from "react";
//Import Scrollbar
import SimpleBar from "simplebar-react";

// MetisMenu
import MetisMenu from "metismenujs";

import { Link } from "react-router-dom";

//i18n
import { withTranslation } from "react-i18next";
import withRouter from "../../Components/Common/withRouter";
import { PermissionsSliceState } from "slices/auth/permissions/reducer";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import Screen from "models/screen";
import capitalize from "utils/capitalize";
import GuardedLink from "Components/atoms/guarded-link";
import useSupportEmail from "hooks/useSupportEmail";

const selectPermissions = createSelector(
  (state: any) => state.Permissions,
  ({ screens }: PermissionsSliceState) => ({
    screens
  })
)

const SidebarContent = (props: any) => {
  const ref = useRef<any>();
  const { screens } = useSelector(selectPermissions)
  const { mailtoHref } = useSupportEmail()
  const iconUrlMap = useMemo(() => {
    const map = new Map<string, string>()
    map.set("executive", "bx bx-briefcase")
    map.set("finance", "bx bx-money")
    map.set("admin", "bx bx-user")
    map.set("human-resources", "bx bx-group")
    map.set("business-development", "bx bx-trending-up")
    map.set("forms", "bx bx-file")


    return map
  }, [])

  const createTreeMap = (screens: Screen[]) => {
    const map = new Map<string, Set<Screen>>();

    if (screens.length === 0) {
      return map
    }

    for (const screen of screens) {
      if (screen.hide) continue
      // Extract the first segment of the URL
      const topLevel = screen.url.split("/")[1] || "root";

      if (!map.has(topLevel)) {
        map.set(topLevel, new Set());
      }

      map.get(topLevel)!.add(screen);
    }

    return map;
  };


  const screenMap = useMemo(() => createTreeMap(screens), [screens])


  const activateParentDropdown = useCallback((item: any) => {
    item.classList.add("active");
    const parent = item.parentElement;
    const parent2El = parent.childNodes[1];

    if (parent2El && parent2El.id !== "side-menu") {
      parent2El.classList.add("mm-show");
    }

    if (parent) {
      parent.classList.add("mm-active");
      const parent2 = parent.parentElement;

      if (parent2) {
        parent2.classList.add("mm-show"); // ul tag

        const parent3 = parent2.parentElement; // li tag

        if (parent3) {
          parent3.classList.add("mm-active"); // li
          parent3.childNodes[0].classList.add("mm-active"); //a
          const parent4 = parent3.parentElement; // ul
          if (parent4) {
            parent4.classList.add("mm-show"); // ul
            const parent5 = parent4.parentElement;
            if (parent5) {
              parent5.classList.add("mm-show"); // li
              parent5.childNodes[0].classList.add("mm-active"); // a tag
            }
          }
        }
      }
      scrollElement(item);
      return false;
    }
    scrollElement(item);
    return false;
  }, []);

  const removeActivation = (items) => {
    for (var i = 0; i < items.length; ++i) {
      var item = items[i];
      const parent = items[i].parentElement;

      if (item && item.classList.contains("active")) {
        item.classList.remove("active");
      }
      if (parent) {
        const parent2El =
          parent.childNodes && parent.childNodes.length && parent.childNodes[1]
            ? parent.childNodes[1]
            : null;
        if (parent2El && parent2El.id !== "side-menu") {
          parent2El.classList.remove("mm-show");
        }

        parent.classList.remove("mm-active");
        const parent2 = parent.parentElement;

        if (parent2) {
          parent2.classList.remove("mm-show");

          const parent3 = parent2.parentElement;
          if (parent3) {
            parent3.classList.remove("mm-active"); // li
            parent3.childNodes[0].classList.remove("mm-active");

            const parent4 = parent3.parentElement; // ul
            if (parent4) {
              parent4.classList.remove("mm-show"); // ul
              const parent5 = parent4.parentElement;
              if (parent5) {
                parent5.classList.remove("mm-show"); // li
                parent5.childNodes[0].classList.remove("mm-active"); // a tag
              }
            }
          }
        }
      }
    }
  };

  const activeMenu = useCallback(() => {
    const pathName = process.env.PUBLIC_URL + props.router.location.pathname;
    let matchingMenuItem = null;
    const ul: any = document.getElementById("side-menu");
    const items = ul.getElementsByTagName("a");
    removeActivation(items);

    for (let i = 0; i < items.length; ++i) {
      // Group toggle anchors (`has-arrow`) have no href, so `.pathname` is ""
      // and `pathName.includes("")` is always true — skip those.
      if (items[i].pathname && pathName.includes(items[i].pathname)) {
        matchingMenuItem = items[i];
        break;
      }
    }
    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem);
    }
  }, [props.router.location.pathname, activateParentDropdown]);

  useEffect(() => {
    ref.current.recalculate();
  }, []);

  useEffect(() => {
    const mm = new MetisMenu("#side-menu");
    return () => (mm as any)?.dispose?.();
  }, [screenMap]);


  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    activeMenu();
  }, [activeMenu]);

  function scrollElement(item: any) {
    if (item) {
      const currentPosition = item.offsetTop;
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300;
      }
    }
  }

  return (
    <React.Fragment>
      <SimpleBar className="h-100" ref={ref}>
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            <li className="menu-title">{props.t("Menu")}</li>
            <li >
              <GuardedLink to={'/dashboard'}>
                <i className={"bx bx-home-circle"}></i>
                <span>{props.t("Dashboard")}</span>
              </GuardedLink>
            </li>


            {Array.from(screenMap.entries()).map(([groupUrl, screensSet]) => {
              // Remove all dashboards
              const screensArray = [...screensSet].filter(s => {
                // Remove leading "/"
                const url = s.url.slice(1);
                return url.split("/").length > 1;
              });

              if (screensArray.length === 0) return null;

              // Use the first screen's top-level "group name" for the nav title
              const navName = groupUrl.split("-").join(" "); // groupName comes from screen metadata

              // Dashboard is treated as top-level without sub-menu
              if (groupUrl === "dashboard") {
                return
              }

              return (
                <li key={groupUrl}>
                  <a className="has-arrow">
                    <i className={iconUrlMap.get(groupUrl) || "bx bx-briefcase"}></i>
                    <span>{props.t(capitalize(navName))}</span>
                  </a>
                  <ul className="sub-menu">
                    {screensArray.map((screen) => (
                      <li key={screen.code}>
                        <GuardedLink to={screen.url}>{props.t(screen.name)}</GuardedLink>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
            <li>
              <GuardedLink to={'/manuals'}>
                <i className={"bx bx-book-open"}></i>
                <span>{props.t("Manuals")}</span>
              </GuardedLink>
            </li>
            {mailtoHref && (
              <li>
                <a href={mailtoHref}>
                  <i className={"bx bx-help-circle"}></i>
                  <span>{props.t("Help")}</span>
                </a>
              </li>
            )}
          </ul>
        </div>
      </SimpleBar>

    </React.Fragment>
  );
};
export default withRouter(withTranslation()(SidebarContent));
